/**
 * Register as a Channel Partner — "Page 1" of the product spec: the
 * eligibility criteria, then the application form.
 *
 * Prefilled from `/me` (an invitation draft or an earlier application) and
 * `/options`. Name and verified mobile are account identity, shown but never
 * sent. A published agreement plus its acceptance is all activation needs;
 * PAN, GSTIN and documents stay optional. The form is always open; only
 * Submit waits for a published agreement (the backend rejects it otherwise).
 */
import * as React from 'react';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, BadgeCheck, CircleCheck, Info, MailPlus } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorMessage,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Spinner,
  toast,
} from '@finmin/ui';
import {
  useChannelPartnerContext,
  useChannelPartnerOptions,
  useSubmitChannelPartnerApplication,
  type ChannelPartnerContext,
  type ChannelPartnerOptions,
} from '@/api/channel-partners';
import {
  applicationDefaults,
  applicationFormSchema,
  buildApplicationPayload,
  type ApplicationFormInput,
} from '../application-schema';
import { AgreementSummary } from './agreement-summary';
import { ChannelPartnerHero } from './cp-hero';
import { ChannelPartnerFieldset } from './profile-fieldset';

const ELIGIBILITY = [
  'Any individual, existing FinminOS user, customer, professional, or business can become a Channel Partner.',
  'You should be able to refer or bring one or more customers or clients to FinminOS through your network, expertise, profession, or relationships.',
  'There is no restriction on profession or category — for example real estate brokers, landlords, bank RMs, wealth managers, business owners, professionals, or existing customers.',
  'You must agree to the FinminOS Channel Partner Agreement.',
  'Final eligibility and activation are subject to approval by FinminOS.',
];

export function ChannelPartnerApplyPage() {
  const me = useChannelPartnerContext();
  const options = useChannelPartnerOptions();

  if (me.isLoading || options.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (me.error || options.error || !me.data || !options.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10 sm:px-6">
        <ErrorMessage
          message={me.error?.message ?? options.error?.message ?? 'Could not load the application form.'}
        />
        <Button
          variant="outline"
          onClick={() => {
            void me.refetch();
            void options.refetch();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!me.data.can_apply) {
    return <Navigate to={me.data.can_access_hub ? '/channel-partner/hub' : '/channel-partner'} replace />;
  }

  return <ApplicationForm context={me.data} options={options.data} />;
}

function ApplicationForm({
  context,
  options,
}: {
  context: ChannelPartnerContext;
  options: ChannelPartnerOptions;
}) {
  const navigate = useNavigate();
  const submit = useSubmitChannelPartnerApplication();
  const [apiError, setApiError] = React.useState<string | null>(null);
  const form = useForm<ApplicationFormInput>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: applicationDefaults(context, options),
    mode: 'onTouched',
  });

  const agreement = options.agreement;
  const agreementId = agreement?.id ?? null;

  // Acceptance belongs to one agreement version: if a newer one is published
  // while the form is open, it must be accepted afresh.
  const acceptedFor = React.useRef(agreementId);
  React.useEffect(() => {
    if (acceptedFor.current === agreementId) return;
    acceptedFor.current = agreementId;
    form.setValue('agreement_accepted', false);
  }, [agreementId, form]);

  const prior = context.application;

  function onSubmit(values: ApplicationFormInput) {
    if (!agreement) return;
    setApiError(null);
    submit.mutate(buildApplicationPayload(values, agreement.id), {
      onSuccess: () => {
        toast.success('Application submitted', 'We will let you know once it has been reviewed.');
        void navigate({ to: '/channel-partner', replace: true });
      },
      onError: (err) => {
        if (err.status === 409) {
          toast.warning('Application not submitted', err.message);
          void navigate({ to: '/channel-partner', replace: true });
          return;
        }
        if (err.status === 422 && /agreement/i.test(err.message)) {
          form.setValue('agreement_accepted', false);
          setApiError('The agreement has been updated. Review the current version and accept it to continue.');
          return;
        }
        setApiError(err.message || 'Could not submit your application. Please try again.');
      },
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <ChannelPartnerHero
        eyebrow="Channel Partner"
        title="Register as a Channel Partner"
        description="Tell us about yourself and accept the Channel Partner Agreement. Our team reviews every application."
      />

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/dashboard"
          className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden />
          Back to Dashboard
        </Link>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg">Eligibility criteria</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-foreground">
              {ELIGIBILITY.map((line) => (
                <li key={line} className="flex gap-2.5">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {prior?.status === 'draft' && (
          <Alert>
            <MailPlus className="h-4 w-4" aria-hidden />
            <AlertTitle>You have been invited</AlertTitle>
            <AlertDescription>
              FinminOS invited you to become a Channel Partner. Complete the form and accept the
              agreement to send it for review.
            </AlertDescription>
          </Alert>
        )}
        {prior?.status === 'rejected' && (
          <Alert>
            <Info className="h-4 w-4" aria-hidden />
            <AlertTitle>Applying again</AlertTitle>
            <AlertDescription>Your earlier details are filled in. Review them before you submit.</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="p-5 sm:p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-8">
                {/* Account identity — shown, never part of the request body. */}
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3">
                  <BadgeCheck className="h-5 w-5 shrink-0 text-success" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {context.account.name || 'Your account'}
                    </p>
                    <p className="text-xs text-muted-foreground">{context.account.mobile} · verified mobile</p>
                  </div>
                  <Badge variant="secondary" className="capitalize">
                    {context.account.role} account
                  </Badge>
                </div>

                <ChannelPartnerFieldset options={options} disabled={submit.isPending} />

                <fieldset className="space-y-3">
                  <legend className="mb-1">
                    <span className="block font-display text-lg font-semibold tracking-tight text-foreground">
                      General Agreement
                    </span>
                  </legend>
                  {agreement ? (
                    <>
                      <AgreementSummary agreement={agreement} />
                      <FormField
                        control={form.control}
                        name="agreement_accepted"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-start gap-3 rounded-md border border-border p-3">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(v) => field.onChange(v === true)}
                                  disabled={submit.isPending}
                                  className="mt-0.5"
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal leading-relaxed text-foreground">
                                I have read and accept the {agreement.title}.
                              </FormLabel>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  ) : (
                    <Alert variant="warning">
                      <Info className="h-4 w-4" aria-hidden />
                      <AlertTitle>Agreement not published yet</AlertTitle>
                      <AlertDescription>
                        Every application accepts the current Channel Partner Agreement, and FinminOS
                        hasn&apos;t published it yet — so the form can be filled in, but it can be
                        submitted only once the agreement is live. Please check back soon.
                      </AlertDescription>
                    </Alert>
                  )}
                </fieldset>

                <ErrorMessage message={apiError} />

                {!agreement && (
                  <p className="text-right text-xs text-muted-foreground">
                    Submit opens once the Channel Partner Agreement is published.
                  </p>
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="outline" asChild>
                    <Link to="/dashboard">Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={!agreement || submit.isPending}>
                    {submit.isPending ? (
                      <>
                        <Spinner className="h-4 w-4" /> Submitting…
                      </>
                    ) : (
                      'Submit application'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
